import PropTypes from 'prop-types'
import React from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components/macro'

import NewTooltip from 'components/atoms/new-tooltip'
import {
  InfoButton,
  InfoIcon,
} from 'components/molecules/price-evaluation-indicator'

/**
 * The grey pricing block of the car file cards. The grid and list variants
 * lay out their definitions differently, but share this container and the
 * label/value pairs inside it.
 */
const CarFileCardPriceBlock = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.sizeByFactor(1.5)};
  padding: ${({ theme }) => theme.sizeByFactor(1.5)};
  background-color: ${({ theme }) => theme.colors.fieldBackground};
  border-radius: ${({ theme }) =>
    theme.cardBorderRadius ? `${theme.cardBorderRadius}px` : '0'};
  font-family: ${({ theme }) => theme.font};
  color: ${({ theme }) => theme.colors.text};
`

const Definition = styled.div`
  display: flex;
  flex-direction: column;
  align-items: ${({ $alignEnd }) => ($alignEnd ? 'flex-end' : 'flex-start')};
  gap: ${({ theme, $gap }) => theme.sizeByFactor($gap)};
  min-width: 0;
`

const Label = styled.span`
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: ${({ theme }) => theme.baseFontSize};
  font-weight: ${({ theme }) => theme.baseFontWeight};
  line-height: ${({ theme }) => theme.baseLineHeight};
  white-space: nowrap;
`

const Value = styled.span`
  font-size: ${({ theme }) => theme.baseFontSize};
  font-weight: 400;
  line-height: ${({ theme }) => theme.baseLineHeight};
  white-space: nowrap;
`

const ValueRow = styled.span`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.sizeByFactor(0.25)};
`

// The info icon sits inside the card link: only show the tooltip, don't open the car file
const preventCardNavigation = (event) => {
  event.preventDefault()
  event.stopPropagation()
}

/**
 * Info icon with a tooltip explaining what the export price includes: BPM is
 * always excluded, VAT is included when the price comes from `exportprijs`
 * and excluded when it comes from `exportprijs_exbtw`.
 */
export const ExportPriceInfo = ({ inclVat }) => {
  const { t } = useTranslation()
  const tooltip = inclVat
    ? t('exportPriceExclBpmInclVat')
    : t('exportPriceExclBpmExclVat')

  return (
    <NewTooltip
      title={tooltip}
      placement="top"
      arrow
      enterTouchDelay={0}
      leaveTouchDelay={3000}
    >
      <InfoButton
        type="button"
        onClick={preventCardNavigation}
        aria-label={tooltip}
      >
        <InfoIcon />
      </InfoButton>
    </NewTooltip>
  )
}

ExportPriceInfo.propTypes = {
  inclVat: PropTypes.bool,
}

ExportPriceInfo.defaultProps = {
  inclVat: false,
}

/** A label with the price below it, optionally followed by an info icon */
export const CarFileCardPriceDefinition = ({
  label,
  value,
  info,
  alignEnd,
  gap,
}) => (
  <Definition $alignEnd={alignEnd} $gap={gap}>
    <Label>{label}</Label>
    {info ? (
      <ValueRow>
        <Value>{value}</Value>
        {info}
      </ValueRow>
    ) : (
      <Value>{value}</Value>
    )}
  </Definition>
)

CarFileCardPriceDefinition.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  info: PropTypes.node,
  alignEnd: PropTypes.bool,
  /** Space between label and value, as a factor of the theme base size */
  gap: PropTypes.number,
}

CarFileCardPriceDefinition.defaultProps = {
  info: null,
  alignEnd: false,
  gap: 0.875,
}

export default CarFileCardPriceBlock
